import React, { useRef, useState } from 'react';
import CameraButtonIcon from '../../../../assets/icons/camera-button-icon.svg';
import UploadButtonIcon from '../../../../assets/icons/upload-button-icon.svg';
import defaultProfileImage from '../../../../assets/images/default-profile-image.jpg';
import { Button } from '../../../../components/button';
import { Card, CardContent } from '../../../../components/card';
import { useToast } from '../../../../components/toast-container';
import { isValidImageFormat } from '../BusinessProfile.common';
import { BUSINESS_PROFILE_NOTIFICATION_MESSAGES } from '../BusinessProfile.const';

interface BusinessLogoSectionProps {
    logoUrl?: string;
    onLogoChange?: (file: File | null) => void;
    onUpload: (file: File) => Promise<void>;
}

export const BusinessLogoSection: React.FC<BusinessLogoSectionProps> = ({
    logoUrl,
    onLogoChange,
    onUpload,
}) => {
    const { addToast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(logoUrl || null);

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file size
        if (file.size > 2 * 1024 * 1024) {
            addToast({
                type: 'error',
                title: 'Validation Error',
                message: BUSINESS_PROFILE_NOTIFICATION_MESSAGES.LOGO_SIZE_ERROR,
            });
            return;
        }

        // Validate file format
        if (!isValidImageFormat(file)) {
            addToast({
                type: 'error',
                title: 'Validation Error',
                message: BUSINESS_PROFILE_NOTIFICATION_MESSAGES.LOGO_FORMAT_ERROR,
            });
            return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);

        onLogoChange?.(file);

        void onUpload(file);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleCameraButtonClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <Card className="border border-[#e2e8f0] dark:border-gray-700 bg-white dark:bg-gray-800 rounded-[8px]">
            <CardContent className="px-[24.8px] py-[25px]">
                <h2 className="text-[24px] font-semibold text-[#020817] dark:text-white tracking-[-0.6px] mb-6">
                    Business Logo
                </h2>

                <div className="flex flex-col items-center justify-center">
                    {/* Avatar với nút camera */}
                    <div className="relative mb-0">
                        {/* Avatar - có thể click để upload */}
                        <button
                            type="button"
                            onClick={handleUploadClick}
                            className="relative w-[128px] h-[128px] rounded-full overflow-hidden bg-[#f5f5f5] dark:bg-gray-700 flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                        >
                            {previewUrl ? (
                                <img
                                    src={previewUrl}
                                    alt="Business Logo"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <img
                                    src={defaultProfileImage}
                                    alt="Default Profile"
                                    className="w-full h-full object-cover dark:opacity-80 dark:brightness-90"
                                />
                            )}
                        </button>
                        {/* Nút Camera ở góc dưới phải - 32x32px */}
                        <button
                            type="button"
                            onClick={handleCameraButtonClick}
                            className="absolute bottom-0 right-0 bg-[#00c29b] dark:bg-emerald-500 rounded-full w-[32px] h-[32px] flex items-center justify-center z-10 cursor-pointer hover:bg-[#00b08a] dark:hover:bg-emerald-600 transition-colors"
                        >
                            <img src={CameraButtonIcon} alt="Camera" className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Upload Info */}
                    <div className="flex flex-col items-center gap-[8px] pt-4">
                        <p className="text-[14px] font-normal text-[#64748b] dark:text-gray-400 text-center leading-[20px]">
                            JPG, PNG or GIF. Max size 2MB.
                        </p>
                        <Button
                            variant="outline"
                            onClick={handleUploadClick}
                            className="h-[36px] px-[12.8px] py-[7.6px] border border-[#e2e8f0] dark:border-gray-700 rounded-[6px] bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 gap-[8px] transition-colors"
                        >
                            <img src={UploadButtonIcon} alt="Upload" className="w-4 h-4 dark:invert dark:opacity-80" />
                            <span className="text-[14px] font-medium text-[#020817] dark:text-gray-200 leading-[20px]">
                                Upload New Photo
                            </span>
                        </Button>
                    </div>

                    {/* Hidden File Input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/gif"
                        onChange={handleFileSelect}
                        className="hidden"
                    />
                </div>
            </CardContent>
        </Card>
    );
};

